import {CloseButton, MantineThemeColors, Tooltip, useSafeMantineTheme} from '@mantine/core'
import {ScaleLinear, scaleLinear, scaleUtc} from 'd3-scale'
import {ReactNode, useState, useMemo, ComponentProps, createContext, useContext} from 'react'
import {QuestionAmountType} from 'shared/forecast'
import {ChildrenProps, StyleProps} from 'shared/ui/uiType'
import {concat, notNullOrEmpty, isNullOrEmpty} from 'shared/collection'
import {doSafe} from 'shared/promise'
import {fmtNum} from 'shared/format'
import {max, min} from 'shared/agg'
import {Rnge} from 'shared/range'
import {Fmt} from 'shared/forecast/Fmt'
import {Markdown, useWindowDim, ThemeOverride, useTip, Tip, UseTip} from 'shared/ui'
import {bin} from 'd3-array'
import {XAxisLabelsProps, XMetricLabelDiv, XAxisLabelsDiv} from './Labels'
import {type TextProps} from '@visx/text'
import {first, isArray, last, sortBy, partition, isEmpty, values, omit} from 'remeda'
import {O} from 'ts-toolbelt'
import {Masonry} from '../ui/Masonry'
import {Expandable} from '../ui/Expandable'
import {useEmSize} from '../ui/useEmSize'
import {P, match} from 'ts-pattern'
import type {NotNull} from '../type'

const {abs} = Math

type ValueBarValue = {
  label?: string
  labelLong?: string
  value: number
  color?: string
}

export type LikelihoodBarProps = {
  scale: ScaleLinear<number, number, never>
  showScale?: boolean
  value: ValueBarValue
  value2?: ValueBarValue
  baseline?: {
    value: number
    color: string
    tip: React.ReactNode
    label?: string
  }
  relative?: boolean
  /** the bar height. Other elements may be added so the total height it bigger than what is given */
  height?: number
  bgProps?: Partial<ComponentProps<'div'>>
}

export const LikelihoodBar = ({
  value,
  value2,
  scale,
  showScale,
  baseline,
  relative,
  height = barHeight,
  bgProps
}: LikelihoodBarProps) => {
  const bgWidth = Rnge.width(scale.range())
  const rectProps = {
    scale,
    relative,
    baseline,
    height,
    alone: value2 == null
  } satisfies Partial<LikelihoodBarRectProps>
  const {colors} = useSafeMantineTheme()
  // its easier to have one label flow and the others position absolutely so that we don't need to calculate height separately
  const largestLabel =
    baseline && (baseline.label || !showScale) ? 'baseline'
    : showScale ? 'scale'
    : undefined
  const format = (v: number) => Fmt.fmt(v, Fmt.optionsLikelihood.absolute.short)
  const layout = useLikelihoodBarRect({...value, ...rectProps})
  const layout2 = value2 ? useLikelihoodBarRect({...value2, ...rectProps}) : null

  const outsideLabels = [...(layout?.outsideLabels ?? []), ...(layout2?.outsideLabels ?? [])]
  return (
    <div id='' className='' style={{display: 'flex', flexDirection: 'column', width: bgWidth, cursor: 'default'}}>
      <div id='labels' className='relative'>
        {showScale && (
          // if we already made room with baseline, then overlay this (absolute)
          // todo need to make the flow to the bottom
          <XAxisLabelsDiv
            {...{
              scale,
              placement: 'book-ends',
              style: largestLabel != 'scale' ? {position: 'absolute', bottom: 0} : {},
              format
            }}
          />
        )}
        {baseline && (
          <XMetricLabelDiv
            {...{
              scale,
              ...baseline,
              place: 'above',
              showTick: true,
              hideLabel: baseline.label == null,
              style: largestLabel != 'baseline' ? {position: 'absolute', bottom: 0} : {},
              format
            }}
            className='text-stroke-white-2'
          />
        )}
      </div>
      <div
        className='relative'
        style={{
          height,
          width: bgWidth,
          backgroundColor: colors.gray[2],
          borderRadius: '5px',
          cursor: 'default',
          ...bgProps?.style
        }}
        {...omit(bgProps ?? {}, ['style'])}>
        <div
          style={{
            position: 'relative',
            transform: `translateX(${scale.range()[0]}px)`,
            cursor: 'default'
          }}>
          {layout && <LikelihoodBarRect {...layout} />}
          {layout2 && <LikelihoodBarRect {...layout2} />}
        </div>
      </div>

      {outsideLabels.length > 0 && (
        <div className='flex justify-between gap-8'>
          <div className='max-w-[70%]'>
            {outsideLabels
              ?.filter(l => l.side === 'left')
              .map((label, i) => (
                <OutsideLabel key={`left-${i}`} label={label} />
              ))}
          </div>
          <div className='max-w-[70%] text-right'>
            {outsideLabels
              ?.filter(l => l.side === 'right')
              .map((label, i) => (
                <OutsideLabel key={`right-${i}`} label={label} />
              ))}
          </div>
        </div>
      )}
    </div>
  )
}

const ValueLabel = ({
  value,
  label,
  labelLong,
  relative,
  getValueLabel,
  color,
  className = ''
}: {
  value: string
  label?: string
  labelLong?: string
  relative?: boolean
  getValueLabel: (rel?: boolean, size?: keyof Fmt.OptionSize) => string
  color?: string
  className?: string
}) => (
  <Tooltip
    withinPortal
    label={
      <div>
        <div className='flex gap-2'>
          <b>{getValueLabel(relative, 'long')}</b>
          {relative && (
            <>
              <span className='opacity-50'> | </span>
              <b>{getValueLabel(!relative, 'long')}</b>
            </>
          )}
        </div>
        {labelLong ?? label}
      </div>
    }>
    <div className={`font-numbers ${className}`} style={{color, cursor: 'default'}}>
      {value}
    </div>
  </Tooltip>
)

const OutsideLabel = ({
  label: l
}: {
  label: {
    text: string
    color: string | undefined
    side: 'left' | 'right'
    type: 'value' | 'label'
    getValueLabel?: (rel?: boolean, size?: keyof Fmt.OptionSize) => string
    relative?: boolean
    labelLong?: string
  }
}) =>
  match(l.type)
    .with('value', () =>
      l.getValueLabel ?
        <ValueLabel
          value={l.text}
          labelLong={l.labelLong}
          relative={l.relative}
          getValueLabel={l.getValueLabel}
          color={l.color}
          className='text-lg font-extrabold leading-tight'
        />
      : <div className='text-lg font-extrabold font-numbers leading-tight' style={{color: l.color, cursor: 'default'}}>
          {l.text}
        </div>
    )
    .with('label', () => (
      <div className='text-md font-semibold leading-tight' style={{color: l.color, cursor: 'default'}}>
        {l.text}
      </div>
    ))
    .exhaustive()

const barHeight = 44
const labelPad = 6

type LikelihoodBarRectProps = ValueBarValue & {
  relative?: boolean
  alone?: boolean
} & Pick<LikelihoodBarProps, 'scale' | 'baseline' | 'height'>

const useLikelihoodBarRect = ({
  value,
  scale,
  baseline,
  relative,
  alone,
  label,
  labelLong,
  color,
  height: heightRaw
}: LikelihoodBarRectProps) => {
  if (value == null) return undefined
  const bgWidth = Rnge.width(scale.range())
  const {colors} = useSafeMantineTheme()
  const emValue = useEmSize({dependencies: [bgWidth]}) // we know if the container is resized so might the font
  const emLabel = useEmSize({dependencies: [bgWidth]})

  const formats = Fmt.getOptions({forecastType: 'likelihood'})

  const getValueLabel = (rel?: boolean, size: keyof Fmt.OptionSize = 'default') =>
    (rel ?? relative) && baseline ?
      Fmt.fmt(value - baseline!.value, formats.relative[size])
    : Fmt.fmt(value, formats.absolute[size])

  const valueLabel = getValueLabel()
  const valueLabelWidth = emValue.getTextWidth(valueLabel)
  const labelWidth = emLabel.getTextWidth(label ?? '')

  const xValue = scale(value)
  const xBaseline = scale(baseline?.value ?? 0)
  const {x, dx} = match(relative)
    .with(true, () => {
      let x = xBaseline
      const dx = xValue - x
      if (dx < 0) x = xValue // x is leftmost and the width is positive. just the way rects are drawn
      return {x, dx}
    })
    .otherwise(() => ({
      x: 0,
      dx: xValue
    }))

  const width = abs(dx)
  const sideDefault = dx < 0 ? 'left' : 'right'
  const bgWidthBySide = {
    left: x - labelPad * 2,
    right: bgWidth - (x + width + labelPad * 2)
  }
  const isValueInside = abs(dx) > valueLabelWidth + 6
  const widthForLabelInside = width - labelPad * 2 - (isValueInside ? valueLabelWidth : 0)

  const height = heightRaw ?? barHeight

  const getLabelProps = (type: 'label' | 'value') => {
    const {side, inside} = match(type)
      .with('label', _ =>
        match(alone)
          .with(true, () => {
            const inside = widthForLabelInside > max(values(bgWidthBySide))
            const side =
              inside ? sideDefault
              : bgWidthBySide.left > bgWidthBySide.right ? 'left'
              : 'right'
            return {side, inside} as const
          })
          .otherwise(
            () =>
              ({
                side: sideDefault,
                inside: isValueInside && widthForLabelInside > bgWidthBySide[sideDefault]
              }) as const
          )
      )
      .with('value', _ => ({side: sideDefault, inside: isValueInside}) as const)
      .exhaustive()

    let props = {
      fill: inside ? colors.gray[0] : color,
      style: {
        opacity: inside ? 0.8 : 1,
        ...(type == 'value' ? {fontWeight: 1000, fontSize: '1.2em'} : {fontWeight: 'bold'})
      },
      verticalAnchor: 'middle' as const,
      y: height / 2,
      ...match(inside)
        .with(true, () => ({
          width: type == 'label' ? widthForLabelInside : width - 2 * labelPad,
          ...match({side, type})
            .with({side: 'left', type: 'label'}, {side: 'right', type: 'value'}, () => ({
              x: x + width - labelPad,
              textAnchor: 'end' as const
            }))
            .with({side: 'right', type: 'label'}, {side: 'left', type: 'value'}, () => ({
              x: x + labelPad,
              textAnchor: 'start' as const
            }))
            .exhaustive()
        }))
        .otherwise(() =>
          match(side)
            .with('left', () => ({
              x: type == 'label' ? 0 + labelPad : x - labelPad,
              textAnchor: type == 'label' ? ('start' as const) : ('end' as const),
              width: x - 2 * labelPad - (isValueInside || type == 'value' ? 0 : valueLabelWidth + labelPad)
            }))
            .with('right', () => ({
              x: type == 'label' ? bgWidth - labelPad : x + width + labelPad,
              textAnchor: type == 'label' ? ('end' as const) : ('start' as const),
              width:
                bgWidth -
                (x + width) -
                2 * labelPad -
                (isValueInside || type === 'value' ? 0 : valueLabelWidth + labelPad)
            }))
            .exhaustive()
        )
    } satisfies Partial<TextProps> // we use TextProps because this logic is complex and was build for svg. We currently translate to styles for divs in the components that use the layout

    // if outside the bounds, move it back inside
    if (!inside && type == 'value') {
      const common = {...props, stroke: 'white', strokeWidth: 2, paintOrder: 'stroke fill'}
      if (side == 'left' && x - valueLabelWidth - labelPad * 2 < 0)
        props = {...common, x: labelPad, textAnchor: 'start'}
      if (side == 'right' && x + width + valueLabelWidth + labelPad * 2 > bgWidth)
        props = {...common, x: bgWidth - labelPad, textAnchor: 'end'}
    }

    return {props, side, inside}
  }

  const valueLabelProps = getLabelProps('value')
  const labelProps = label ? getLabelProps('label') : null

  // Check if labels should be rendered outside (below the bar)
  const shouldRenderLabelOutside =
    label && labelProps && (labelProps.props.width! < 30 || labelWidth + 2 * labelPad > 2.2 * labelProps.props.width!)
  const shouldRenderValueLabelOutside =
    valueLabelProps.props.width! < 30 || valueLabelWidth + 2 * labelPad > 2.5 * valueLabelProps.props.width!
  const labelPropsValue = {
    ...valueLabelProps.props,
    display: shouldRenderValueLabelOutside ? ('none' as const) : undefined
  }
  const labelPropsLabel =
    label && labelProps ?
      {
        ...labelProps.props,
        display: shouldRenderLabelOutside ? ('none' as const) : undefined
      }
    : null
  const outsideLabels = []
  if (shouldRenderValueLabelOutside)
    outsideLabels.push({
      text: valueLabel,
      color,
      side: valueLabelProps.side,
      type: 'value' as const,
      getValueLabel,
      relative,
      labelLong
    })
  if (shouldRenderLabelOutside && labelProps)
    outsideLabels.push({text: label, color, side: labelProps.side, type: 'label' as const})

  return {
    x,
    width,
    height,
    labelPropsValue,
    labelPropsLabel,
    outsideLabels,
    valueLabel,
    emValue,
    emLabel,
    getValueLabel,
    relative,
    color,
    labelLong,
    label
  }
}
type UseLikelihoodBarRect = NotNull<ReturnType<typeof useLikelihoodBarRect>>

const LikelihoodBarRect = ({
  x,
  width,
  height,
  labelPropsValue,
  labelPropsLabel,
  valueLabel,
  emValue,
  emLabel,
  getValueLabel,
  relative,
  color,
  labelLong,
  label
}: UseLikelihoodBarRect) => {
  const getLabelStyle = (s: NonNullable<typeof labelPropsValue>) => ({
    position: 'absolute' as const,
    left: s.x,
    top: s.y,
    width: s.width,
    color: s.fill,
    opacity: s.style?.opacity,
    fontWeight: s.style?.fontWeight,
    fontSize: (s.style as {fontSize?: string})?.fontSize,
    textAlign:
      s.textAnchor === 'start' ? ('left' as const)
      : s.textAnchor === 'end' ? ('right' as const)
      : ('center' as const),
    transform: `${s.textAnchor === 'end' ? 'translateX(-100%)' : ''} translateY(-50%)`,
    lineHeight: '1em',
    cursor: 'default',
    display: s.display
  })

  return (
    <>
      <div
        id='bar-value'
        className='absolute'
        style={{
          left: x,
          top: 0,
          width: width,
          height: height,
          backgroundColor: color,
          borderRadius: '5px',
          cursor: 'default'
        }}
      />

      {/* Always render value label for measurement */}
      <div style={getLabelStyle(labelPropsValue!)} ref={emValue.ref}>
        <ValueLabel
          {...{value: valueLabel, label, labelLong, relative, getValueLabel}}
          color={labelPropsValue?.fill}
          className='font-numbers'
        />
      </div>

      {/* Always render label for measurement */}
      {label && (
        <div style={getLabelStyle(labelPropsLabel!)} ref={emLabel.ref}>
          {label}
        </div>
      )}
    </>
  )
}

export const binForecastScale = ({
  bins,
  color,
  colors
}: {
  bins: BinForecasts['bins']
  color?: string | undefined
  colors: MantineThemeColors
  baseline?: number
}) =>
  scaleLinear(
    [0, max(bins.map(({forecasts}) => forecasts.length))],
    ['rgba(1,1,1,0)', color ?? 'var(--mantine-color-primary-filled)']
  )

export type BinForecast = {value: number; valueRelative?: number; notes?: string}
export type Bin<T extends BinForecast = BinForecast> = {i: number; x0: number; x1: number; forecasts: T[]}
export type BinForecasts<T extends BinForecast = BinForecast> = {
  bins: {
    i: number
    x0: number
    x1: number
    forecasts: T[]
  }[]
  tails?: {
    left: {forecasts: T[]; x: number}
    right: {forecasts: T[]; x: number}
  }
}
export const isForecastBinsEmpty = (b: BinForecasts<any>) => isNullOrEmpty(b.bins) && isNullOrEmpty(b.tails)

export const binForecast = <T extends BinForecast>({
  domain: domainRaw,
  forecasts: forecastsRaw,
  bins: numBins = 20,
  amountType = 'number',
  debugName
}: {
  domain: [number, number] | ScaleLinear<number, number>
  forecasts: T[]
  bins?: number
  amountType?: QuestionAmountType
  debugName?: string
}): BinForecasts<T> => {
  const forecasts = forecastsRaw.filter(f => f.value != null)
  const domain = isArray(domainRaw) ? domainRaw : (domainRaw.domain() as [number, number])
  if (!Rnge.isAscending(domain)) return {bins: []}
  let forecastsToAdd = [...forecasts]
  const bins = match(amountType)
    .with('number', () => bin().domain(domain).thresholds(numBins))
    .with('date', () =>
      bin()
        .domain(domain)
        .thresholds((_, min, max) =>
          scaleUtc()
            .domain([min, max])
            .ticks(numBins)
            .map(d => d.getTime())
        )
    )
    .exhaustive()(forecasts.map(f => f.value))
    .map((b, i) => {
      // find the forecasts for this bin and take them out of the list
      const [inBin, toAdd] = partition(forecastsToAdd, f => f.value >= b.x0! && f.value < b.x1!)
      forecastsToAdd = toAdd
      return {
        i,
        x0: b.x0!,
        x1: b.x1!,
        forecasts: inBin
      }
    })

  const [left, right] = partition(forecastsToAdd, f => f.value < domain[0])
  const res = {
    bins: bins.filter(b => !isEmpty(b.forecasts)),
    tails: {left: {forecasts: left, x: domain[0]}, right: {forecasts: right, x: domain[1]}}
  }
  return res
}

export type ForecastBinBarProps<T extends BinForecast = BinForecast> = {
  value?: number
  bins: BinForecasts<T>['bins']
  showScale?: boolean
  showValue?: boolean
  showTick?: boolean
  valueSize?: string
  color?: string
  axisLabelsPlacement?: XAxisLabelsProps['placement']
  showHelp?: boolean
} & Pick<BinRectProps<T>, 'noteCustom' | 'format' | 'label' | 'scaleBinColor' | 'scale'>

/** forecast bucketed into bins, optionally with a value on top and axis labels */
export const ForecastBinBar = <T extends BinForecast = BinForecast>({
  value,
  format = Fmt.optionsLikelihood,
  bins,
  scaleBinColor,
  scale,
  showScale,
  showValue = true,
  showTick,
  valueSize = '2em',
  color,
  axisLabelsPlacement,
  noteCustom,
  showHelp,
  label
}: ForecastBinBarProps<T>) => {
  const {colors} = useSafeMantineTheme()

  if (isForecastBinsEmpty({bins})) return <></>

  const helpX =
    showHelp ?
      doSafe(
        first(sortBy(bins, [b => b.forecasts.filter(f => f.notes).length, 'desc'], [b => b.forecasts.length, 'desc'])),
        g => scale((g.x0 + g.x1) / 2)
      )
    : undefined
  const textDim =
    helpX ?
      fitX(
        {x: helpX - 10, width: 500, minWidth: 300},
        {x: scale.range()[0]! + 10, width: Rnge.width(scale.range()) - 30}
      )
    : undefined
  return (
    <div className='flex flex-col relative'>
      {showTick && value != null && <BinBarTick {...{scale, color, value}} />}
      {showValue && value != null && (
        <XMetricLabelDiv
          {...{
            scale,
            format: v => Fmt.fmt(v, format.absolute.default),
            place: 'above',
            showTick: false,
            size: valueSize ?? '0.9em',
            color: color ?? colors.gray[5],
            value
          }}
        />
      )}
      <div className='h-[11px] relative  w-full left-0 top-0 z-1'>
        {bins.map(b => (
          <BinRect key={b.i} {...{...b, scaleBinColor, scale, format, noteCustom}} />
        ))}
      </div>
      {showScale && (
        <XAxisLabelsDiv
          scale={scale}
          format={v => Fmt.fmt(v, format.absolute.short)}
          placement={axisLabelsPlacement}
          showTick
          styles={{tick: {width: '1px'}}}
        />
      )}
      {helpX && textDim && (
        <div className='' style={{marginTop: showScale ? '-2.1em' : ''}}>
          <svg className='relative inline-block size-[16px]' style={{left: helpX, transform: `translateX(-50%)`}}>
            <path
              fill={colors.gray[4]}
              d='M12 3.172l-6.414 6.414c-.781.781-.781 2.047 0 2.828s2.047.781 2.828 0l1.586-1.586v7.242c0 1.104.895 2 2 2 1.104 0 2-.896 2-2v-7.242l1.586 1.586c.391.391.902.586 1.414.586s1.023-.195 1.414-.586c.781-.781.781-2.047 0-2.828l-6.414-6.414z'
              transform='scale(0.8, 0.8)'
            />
          </svg>
          <div
            className='relative opacity-40 text-[0.9em] leading-tight mt-[-0.2em]'
            style={{left: textDim.x, width: textDim.width}}>
            Hover/tap to see comments and rationales from forecasters within each range
          </div>
        </div>
      )}
    </div>
  )
}

const BinBarTick = ({
  value,
  scale,
  color,
  className,
  style
}: {value: number; color?: string} & Pick<BinRectTargetProps, 'scale'> & StyleProps) => {
  const {colors} = useSafeMantineTheme()
  const x = scale(value)
  return (
    <div
      id='tick'
      className={`absolute w-[4px] h-[18px] rounded-md top-[-3px] ${className ?? ''}`}
      style={{
        left: x,
        backgroundColor: color ?? colors.gray[6],
        opacity: 0.8,
        transform: 'translateX(-50%)',
        ...style
      }}></div>
  )
}

type Rect = {x: number; y: number; width: number; height: number}
type RectX = Pick<Rect, 'x' | 'width'>

/** fit r inside the container. if minWidth is specified, it will be move left */
const fitX = (r: RectX & {minWidth?: number}, container: RectX): RectX => {
  let x = max([r.x, container.x])
  let width = min([r.x + r.width, container.x + container.width]) - x // width without moving x
  const minWidth = min(concat(r.minWidth, container.width))
  if (r.minWidth && width < r.minWidth) {
    // if it is less than the optimal size, move it left
    x = container.x + container.width - minWidth
    width = minWidth
  }
  return {...r, x, width}
}

export type BinRectProps<T extends BinForecast = BinForecast> = Omit<BinWrapProps<T>, 'children' | 'tipTitle'> &
  Bin<T> &
  Pick<BinRectTargetProps, 'scale' | 'scaleBinColor'>
export const BinRect = <T extends BinForecast>(props: BinRectProps<T>) => {
  const {forecasts, x0, x1, scale, scaleBinColor} = props
  const format = props.format ?? Fmt.optionsLikelihood
  return (
    <BinWrap
      {...props}
      tipTitle={
        <div className='flex flex-wrap gap-4'>
          <div>
            <b>{Fmt.fmt(x0, format.absolute.short)}</b> to <b>{Fmt.fmt(x1, format.absolute.long)}</b>
          </div>
        </div>
      }>
      {({opened, setOpened}) => (
        <BinRectTarget
          numForecasts={forecasts.length}
          {...{scale, scaleBinColor, x0, x1}}
          onClick={() => setOpened(true)}
        />
      )}
    </BinWrap>
  )
}

export type BinWrapProps<T extends BinForecast = BinForecast> = {
  format?: Fmt.Options
  noteCustom?: (forecast: O.Required<T, 'notes'>) => ReactNode
  label?: string
  tipTitle?: ReactNode
  forecasts: T[]
  hideValueLabels?: boolean
  children: ((props: {opened: boolean; setOpened: (opened: boolean) => void}) => ReactNode) | ReactNode
}
export const BinWrap = <T extends BinForecast>(props: BinWrapProps<T>) => {
  const {colors} = useSafeMantineTheme()

  const {forecasts: _forecasts, noteCustom, label, tipTitle, children, hideValueLabels} = props
  const format = props.format ?? Fmt.optionsLikelihood

  type ForecastGroup = {forecasts: T[]; noteForecast?: O.Required<T, 'notes'>}
  // after sorting we want to group consecutive no-note forecasts for compact display
  const groupedForecasts = useMemo(
    () =>
      sortBy(_forecasts, f => f.value).reduce((acc: ForecastGroup[], f: T) => {
        const l = last(acc)
        if (l == null) return [{forecasts: [f], noteForecast: notNullOrEmpty(f.notes) ? f : undefined} as ForecastGroup]
        const hasNote = notNullOrEmpty(f.notes)
        if (l.noteForecast && hasNote) return [...acc, {forecasts: [f], noteForecast: f} as ForecastGroup]
        if (l.noteForecast == null && hasNote) l.noteForecast = f as O.Required<T, 'notes'> // make this the note forecast for this group
        l.forecasts.push(f)
        return acc
      }, []),
    [_forecasts]
  )

  const {w: screenWidth} = useWindowDim()

  const tip = useTip()
  const backgroundColor = colors.gray?.[9]
  const colWidthBasis = 400
  const tipWidth = min([screenWidth - 60, max([(colWidthBasis + 10) * groupedForecasts.length, 400])])

  return (
    <Tip
      store={tip}
      position='bottom'
      offset={0}
      zIndex={1000}
      transitionProps={{transition: 'pop'}}
      styles={{tooltip: {backgroundColor, width: tipWidth}}}
      classNames={{
        tooltip: 'max-h-[50vh] overflow-y-auto overflow-x-clip whitespace-normal'
      }}
      label={
        <div>
          <ThemeOverride override={{colorScheme: 'dark'}}>
            <div className='mb-2 flex items-start justify-between gap-6'>
              <div className='grow'>
                {tipTitle}
                {label && <div className='opacity-75'>{label}</div>}
              </div>
              <div>
                <b>{fmtNum(_forecasts.length)}</b> {_forecasts.length == 1 ? 'forecast' : 'forecasts'}
              </div>
              <div className=''>
                <CloseButton
                  onClick={() => {
                    tip.setOpened(false)
                  }}
                />
              </div>
            </div>
            {groupedForecasts.length > 0 && (
              <NotesContext.Provider value={{hideValueLabels, format, setTipOpened: tip.setOpened}}>
                <Masonry colWidthBasis={colWidthBasis} maxColWidth='40em'>
                  {groupedForecasts.map(({noteForecast, forecasts}, i) => {
                    const noteKey = `note-${i}`
                    // for compactness the no-note forecasts are grouped together. The custom component is only for notes
                    return match(noteForecast)
                      .with(P.nullish, () =>
                        hideValueLabels ?
                          <></>
                        : <div key={noteKey} className='flex flex-wrap gap-2'>
                            {forecasts.map((f, i) => (
                              <ValueTag key={i} className={notNullOrEmpty(f.notes) ? 'bg-slate-500/50' : ''}>
                                <>
                                  <span>
                                    {Fmt.fmt(f.value!, format.absolute[f.valueRelative != null ? 'short' : 'default'])}
                                  </span>
                                  {f.valueRelative != null && (
                                    <>
                                      <span className='opacity-50 px-1 font-normal'>|</span>
                                      <span>{Fmt.fmt(f.valueRelative, format.relative.default)}</span>
                                    </>
                                  )}
                                </>
                              </ValueTag>
                            ))}
                          </div>
                      )
                      .otherwise(n =>
                        noteCustom != null ? noteCustom(n) : <ForecastBarNote key={noteKey} forecast={n} />
                      )
                  })}
                </Masonry>
              </NotesContext.Provider>
            )}
          </ThemeOverride>
        </div>
      }>
      {typeof children == 'function' ? children({opened: tip.opened, setOpened: tip.setOpened}) : children}
    </Tip>
  )
}

const NotesContext = createContext<
  {hideValueLabels?: boolean; format: Fmt.Options; setTipOpened: UseTip['setOpened']} | undefined
>(undefined)

const NoteValueTag = ({forecast: f}: {forecast: BinForecast}) => {
  const {format} = useContext(NotesContext)!
  return (
    <ValueTag className={notNullOrEmpty(f.notes) ? 'bg-slate-500/50' : ''}>
      <>
        <span>{Fmt.fmt(f.value!, format.absolute[f.valueRelative != null ? 'short' : 'default'])}</span>
        {f.valueRelative != null && (
          <>
            <span className='opacity-50 px-1 font-normal'>|</span>
            <span>{Fmt.fmt(f.valueRelative, format.relative.default)}</span>
          </>
        )}
      </>
    </ValueTag>
  )
}

/** displayes a forecast note with an optional additional menu and an override for content */
export const ForecastBarNote = ({
  forecast: f,
  menu,
  content
}: {
  forecast: O.Required<BinForecast, 'notes'>
  menu?: ReactNode
  content?: ReactNode
}) => {
  const [expanded, setExpanded] = useState(false)
  const {hideValueLabels, setTipOpened} = useContext(NotesContext)!
  return (
    <div>
      <div className='flex gap-2'>
        <div className='flex flex-wrap gap-2'>{!hideValueLabels && <NoteValueTag forecast={f} />}</div>
        {menu}
      </div>
      {match(content)
        .with(
          P.nullish,
          P.string,
          s =>
            (s || f.notes) && (
              <Expandable
                maxHeight='170px'
                hideScroll
                expanded={expanded}
                toggle={() => {
                  setExpanded(prev => !prev)
                }}
                onChange={expanded => {
                  setExpanded(expanded)
                  setTipOpened(true)
                }}>
                <Markdown className='mr-1 mt-1 break-words'>{s ?? f.notes ?? ''}</Markdown>
              </Expandable>
            )
        )
        .otherwise(e => e)}
    </div>
  )
}

const ValueTag = ({children, className}: ChildrenProps & StyleProps) => (
  <div
    className={`inline-flex items-center justify-center rounded-full px-2 py-1 text-xs font-bold text-gray-200 bg-slate-500/20 ${className}`}>
    {children}
  </div>
)

type BinRectTargetProps = Pick<Bin, 'x0' | 'x1'> & {
  numForecasts: number
  onClick: () => void
  scaleBinColor: ScaleLinear<string, string>
  scale: ScaleLinear<number, number>
}

const BinRectTarget = ({numForecasts, x0, x1, scaleBinColor, scale, onClick, ...props}: BinRectTargetProps) => (
  <div
    {...props}
    className='rounded-md absolute'
    style={{
      left: scale(x0!) + 0.5,
      width: abs(scale(x1!) - scale(x0!) - 1),
      height: 10,
      backgroundColor: scaleBinColor(numForecasts)
    }}
    onClick={onClick}
  />
)
